import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getMonthlyGoals,
  saveMonthlyGoal,
  archiveAndRollForwardGoals,
  getMonthlyActivityCounts,
} from '../../../firebase/services';

const getCurrentMonthString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthLabel = () => {
  return new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const MonthlyGoalsCard = ({ causes, userId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [goals, setGoals] = useState({});
  const [activityCounts, setActivityCounts] = useState({});
  const [editingCause, setEditingCause] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentMonthStr = getCurrentMonthString();
        const now = new Date();
        const year = now.getFullYear();
        const monthIndex = now.getMonth();
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = new Date(year, monthIndex + 1, 1);
        const causeTitles = causes.map(c => c.title);

        let goalsDoc = await getMonthlyGoals(userId);

        // Rollover: document exists but is from a previous month
        if (goalsDoc && goalsDoc.month !== currentMonthStr) {
          const prevYear = parseInt(goalsDoc.month.split('-')[0]);
          const prevMonthIndex = parseInt(goalsDoc.month.split('-')[1]) - 1;
          const prevMonthStart = new Date(prevYear, prevMonthIndex, 1);
          const prevMonthEnd = new Date(prevYear, prevMonthIndex + 1, 1);
          const prevCounts = await getMonthlyActivityCounts(userId, causeTitles, prevMonthStart, prevMonthEnd);
          await archiveAndRollForwardGoals(userId, goalsDoc.month, goalsDoc.goals || {}, prevCounts, currentMonthStr);
          goalsDoc = await getMonthlyGoals(userId);
        }

        const counts = await getMonthlyActivityCounts(userId, causeTitles, monthStart, monthEnd);
        setGoals(goalsDoc?.goals || {});
        setActivityCounts(counts);
      } catch (error) {
        console.error('MonthlyGoalsCard: Failed to load goals data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (causes && causes.length > 0) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [userId, causes]);

  const handleStartEdit = (causeTitle, currentGoal) => {
    setEditingCause(causeTitle);
    setEditValue(String(currentGoal));
  };

  const handleSaveEdit = async (causeTitle) => {
    try {
      const parsed = parseInt(editValue, 10);
      const clamped = isNaN(parsed) ? 0 : Math.min(99, Math.max(0, parsed));
      const currentMonthStr = getCurrentMonthString();
      await saveMonthlyGoal(userId, causeTitle, clamped, currentMonthStr);
      setGoals(prev => ({ ...prev, [causeTitle]: clamped }));
    } catch (error) {
      console.error('MonthlyGoalsCard: Failed to save goal:', error);
    } finally {
      setEditingCause(null);
    }
  };

  if (loading) return null;

  const monthLabel = getMonthLabel();
  const totalGoal = Object.values(goals).reduce((sum, v) => sum + (v || 0), 0);
  const totalCount = Object.values(activityCounts).reduce((sum, v) => sum + (v || 0), 0);

  return (
    <div style={{ background: 'white', border: '1px solid #eee', borderRadius: '10px', padding: '14px 16px 12px' }}>
      {/* Header */}
      <div style={{ marginBottom: '14px' }}>
        <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px', color: '#000033' }}>
          {t('goals.title')}: {monthLabel}
        </span>
      </div>

      {/* Cause rows */}
      {causes.map((cause, index) => {
        const count = activityCounts[cause.title] || 0;
        const goal = goals[cause.title] || 0;
        const isMet = goal > 0 && count >= goal;
        const fillPercent = goal > 0 ? Math.min(100, (count / goal) * 100) : 0;
        const barColor = isMet ? '#4CAF50' : goal > 0 ? '#FFD100' : '#E0E0E0';
        const textColor = isMet ? '#4CAF50' : goal > 0 ? '#000033' : '#808080';
        const isEditing = editingCause === cause.title;
        const isLast = index === causes.length - 1;

        return (
          <div key={cause.title} style={{ marginBottom: isLast ? '6px' : '12px' }}>
            <div
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 10px', marginLeft: '-10px', marginRight: '-10px',
                borderRadius: '6px', cursor: 'pointer',
                background: isEditing ? '#FFFDE7' : 'transparent',
              }}
              onClick={() => { if (!isEditing) handleStartEdit(cause.title, goal); }}
            >
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#000033', flex: 1, paddingRight: '12px' }}>
                {cause.title}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {isEditing ? (
                  <span style={{ fontSize: '13px', fontWeight: 600, color: textColor, whiteSpace: 'nowrap' }}>
                    {count}
                    <span style={{ color: '#808080', fontWeight: 400 }}> / </span>
                    <input
                      type="number"
                      min={0}
                      max={99}
                      value={editValue}
                      autoFocus
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleSaveEdit(cause.title)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(cause.title); }}
                      style={{
                        width: '40px', height: '26px', textAlign: 'center',
                        border: '2px solid #FFD100', borderRadius: '4px',
                        fontSize: '13px', fontWeight: 600, color: '#000033',
                        outline: 'none', fontFamily: 'Inter, sans-serif',
                        padding: 0, background: 'white',
                      }}
                    />
                  </span>
                ) : (
                  <>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: textColor, whiteSpace: 'nowrap' }}>
                      {count}
                      <span style={{ color: '#808080', fontWeight: 400 }}> / </span>
                      {goal}
                    </span>
                    <svg width="12" height="12" viewBox="0 0 12 12" style={{ flexShrink: 0, opacity: 0.3 }}>
                      <path d="M4 2l4 4-4 4" stroke="#000" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </div>
            </div>
            {/* Progress bar */}
            <div style={{ width: '100%', height: '4px', background: '#E0E0E0', borderRadius: '2px', overflow: 'hidden', marginTop: '4px' }}>
              <div style={{ width: `${fillPercent}%`, height: '100%', background: barColor, borderRadius: '2px', transition: 'width 0.3s ease, background 0.3s ease' }} />
            </div>
          </div>
        );
      })}

      {/* Total row */}
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '10px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#808080' }}>
          {totalGoal > 0
            ? t('goals.totalWithGoal', { count: totalCount, goal: totalGoal })
            : t('goals.totalNoGoal', { count: totalCount })}
        </span>
        <span
          style={{ fontSize: '12px', color: '#007bff', cursor: 'pointer' }}
          onClick={() => navigate('/past-goals')}
        >
          {t('goals.pastGoals')}
        </span>
      </div>
    </div>
  );
};

export default MonthlyGoalsCard;
